import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, ChevronDown } from 'lucide-react';
import { MetaTags } from '../../../../components/SEO/MetaTags';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
export function BlogPostEmotionalDrainFR() {
  const articleData = {
    title: "Pourquoi certaines personnes nous épuisent-elles ? (L'IA l'explique)",
    description: "Vous vous sentez émotionnellement épuisé par les SMS ? Découvrez comment l'IA de Toxic Detector détecte la communication unilatérale et les schémas subtils d'épuisement émotionnel dans vos relations.",
    slug: 'emotional-drain-relationships-ai',
    publishDate: '2025-09-03',
    author: 'Rédaction Toxic Detector'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: "Qu'est-ce que l'épuisement émotionnel dans les relations ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "L'épuisement émotionnel survient lorsque les interactions avec quelqu'un vous laissent constamment fatigué, frustré ou coupable, souvent en raison d'une communication unilatérale ou manipulatrice."
      }
    }, {
      '@type': 'Question',
      name: 'Comment les SMS peuvent-ils causer un épuisement émotionnel ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Des messages unilatéraux répétés, la culpabilisation, un engagement incohérent ou une critique subtile peuvent vous laisser épuisé, même sans conflit majeur.'
      }
    }, {
      '@type': 'Question',
      name: "L'IA peut-elle détecter qui draine mon énergie émotionnelle ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Oui. Toxic Detector analyse les schémas de texte, le ton et l'engagement pour mettre en évidence instantanément les comportements unilatéraux, manipulateurs ou épuisants."
      }
    }]
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://toxicdetector.com/images/logo.png',
    author: {
      '@type': 'Person',
      name: articleData.author
    },
    datePublished: articleData.publishDate,
    url: `https://toxicdetector.com/fr/blog/${articleData.slug}`
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papiers !');
    }
  };
  return <>
      <MetaTags title="Pourquoi certaines personnes nous épuisent-elles ? — IA explique l'épuisement émotionnel | ToxicDetector" description="Vous vous sentez émotionnellement épuisé par les SMS ? Découvrez comment l'IA de Toxic Detector détecte la communication unilatérale et les schémas subtils d'épuisement émotionnel." keywords="épuisement émotionnel dans les relations, schémas de texte unilatéraux, vampires énergétiques, analyse émotionnelle IA" canonical={`https://toxicdetector.com/fr/blog/${articleData.slug}`} ogType="article" ogImage="https://cdn.magicpatterns.com/uploads/dA7JvMmr79gFQy3nZ9PyWw/file_00000000ee5472468a16f1060b3742e2.png" schema={[faqSchema, articleSchema]} data-id="element-5762" />

      <div className="flex flex-col w-full min-h-screen bg-white" data-id="element-5763">
        <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50" data-id="element-5764">
          <div className="max-w-4xl mx-auto px-6 py-5" data-id="element-5765">
            <div className="flex items-center justify-between" data-id="element-5766">
              <Link to="/" className="flex items-center gap-2 group" data-id="element-5767">
                <span className="text-2xl" data-id="element-5768">🚩</span>
                <span className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors" data-id="element-5769">
                  ToxicDetector
                </span>
              </Link>
              <div className="flex items-center gap-3" data-id="element-5770">
                <Link to="/signup" className="hidden sm:inline-block bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-red-700 transition-all shadow-sm hover:shadow-md" data-id="element-5771">
                  Scan Gratuit
                </Link>
                <button onClick={handleShare} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Partager l'article" data-id="element-5772">
                  <Share2 size={20} className="text-gray-600" data-id="element-5773" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <article className="max-w-4xl mx-auto w-full px-6 py-16" data-id="element-5774">
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-12 hover:text-gray-900 transition-colors group" data-id="element-5775">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-5776" />
            Retour à l'Accueil
          </Link>

          <header className="mb-16" data-id="element-5777">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight leading-[1.1] text-gray-900" data-id="element-5778">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-500 text-sm gap-3" data-id="element-5779">
              <time dateTime={articleData.publishDate} data-id="element-5780">
                {new Date(articleData.publishDate).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-5781">•</span>
              <span data-id="element-5782">{articleData.author}</span>
            </div>
          </header>

          <div className="mb-16" data-id="element-5783">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ajgMhQjEKnfJfWgPd5sqFq/1763122862210.jpg" alt="IA analysant les schémas d'épuisement émotionnel dans les SMS" className="w-full rounded-2xl shadow-lg" data-id="element-5784" />
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 mb-16" data-id="element-5785">
            <h2 className="font-bold text-xl mb-6 text-gray-900" data-id="element-5786">
              Table des Matières
            </h2>
            <ol className="space-y-3.5 text-base list-decimal list-inside" data-id="element-5787">
              <li data-id="element-5788">
                <a href="#always-something-wrong" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5789">
                  L'ami "Il y a toujours quelque chose qui ne va pas"
                </a>
              </li>
              <li data-id="element-5790">
                <a href="#only-when-they-need" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5791">
                  La personne qui n'écrit que quand elle a besoin de quelque
                  chose
                </a>
              </li>
              <li data-id="element-5792">
                <a href="#dry-texting" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5793">
                  Le "Dry-Texter" sans émotion
                </a>
              </li>
              <li data-id="element-5794">
                <a href="#ghost-return" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5795">
                  L'artiste du Ghosting & Retour
                </a>
              </li>
              <li data-id="element-5796">
                <a href="#guilt-tripper" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5797">
                  Le culpabilisateur subtil
                </a>
              </li>
              <li data-id="element-5798">
                <a href="#faux-supporter" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5799">
                  Le faux supporter
                </a>
              </li>
              <li data-id="element-5800">
                <a href="#healthy-toxic-combo" className="text-gray-700 hover:text-red-600 transition-colors font-medium" data-id="element-5801">
                  Le combo "Sain-Toxique"
                </a>
              </li>
            </ol>
          </div>

          <div className="prose prose-lg max-w-none" data-id="element-5802">
            <p className="text-2xl mb-10 leading-relaxed text-gray-700 font-medium" data-id="element-5803">
              Avez-vous déjà envoyé un SMS à quelqu'un et vous êtes-vous senti
              fatigué, agacé ou étrangement coupable après, même si la
              conversation n'était pas une dispute ? C'est{' '}
              <strong className="text-gray-900" data-id="element-5804">
                l'épuisement émotionnel dans les relations
              </strong>
              , et c'est plus courant que vous ne le pensez.
            </p>
            <p className="mb-16 text-lg leading-relaxed text-gray-600" data-id="element-5805">
              Ce sont des{' '}
              <strong className="text-gray-900" data-id="element-5806">
                schémas de texte unilatéraux
              </strong>{' '}
              et une manipulation subtile. Notre{' '}
              <strong className="text-gray-900" data-id="element-5807">analyse IA</strong> lit ces
              schémas instantanément.
            </p>

            <section id="always-something-wrong" className="mb-16" data-id="element-5808">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5809">
                1️⃣ L'ami "Il y a toujours quelque chose qui ne va pas"
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-gray-600" data-id="element-5810">
                Vous regardez votre téléphone et ressentez instantanément :
              </p>
              <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-8 rounded-r-xl" data-id="element-5811">
                <p className="italic text-gray-900 text-xl font-medium" data-id="element-5812">
                  "Oh mon Dieu... quoi maintenant ?"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5813">
                Chaque message est du stress, du chaos ou des plaintes. L'IA
                marque cela comme une{' '}
                <strong className="text-gray-900" data-id="element-5814">
                  charge émotionnelle unilatérale
                </strong>
                .
              </p>
            </section>

            <section id="only-when-they-need" className="mb-16" data-id="element-5815">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5816">
                2️⃣ La personne qui n'écrit que quand elle a besoin de quelque
                chose
              </h2>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 mb-8" data-id="element-5817">
                <p className="mb-4 text-lg text-gray-700" data-id="element-5818">
                  <strong className="text-gray-900" data-id="element-5819">Jamais :</strong> "Comment
                  vas-tu ?"
                </p>
                <p className="text-lg text-gray-700" data-id="element-5820">
                  <strong className="text-gray-900" data-id="element-5821">Toujours :</strong>{' '}
                  "Peux-tu... ?"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5822">
                L'IA détecte cela comme une{' '}
                <strong className="text-gray-900" data-id="element-5823">
                  communication transactionnelle
                </strong>
                .
              </p>
            </section>

            <section id="dry-texting" className="mb-16" data-id="element-5824">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5825">
                3️⃣ Le "Dry-Texter" sans émotion
              </h2>
              <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-8 rounded-r-xl" data-id="element-5826">
                <p className="italic text-gray-900 text-xl font-medium" data-id="element-5827">
                  "ok."
                  <br data-id="element-5828" />
                  "K."
                  <br data-id="element-5829" />
                  "👍"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600 mb-8" data-id="element-5830">
                L'IA capte ces{' '}
                <strong className="text-gray-900" data-id="element-5831">
                  signaux de déconnexion
                </strong>{' '}
                instantanément.
              </p>
            </section>

            <section id="ghost-return" className="mb-16" data-id="element-5832">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5833">
                4️⃣ L'artiste du Ghosting & Retour
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-gray-600" data-id="element-5834">
                Disparaît pendant 3 semaines. Revient comme :
              </p>
              <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-8 rounded-r-xl" data-id="element-5835">
                <p className="italic text-gray-900 text-xl font-medium" data-id="element-5836">
                  "saluuut 😁"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5837">
                L'IA marque cela comme un{' '}
                <strong className="text-gray-900" data-id="element-5838">engagement incohérent</strong>
                .
              </p>
            </section>

            <section id="guilt-tripper" className="mb-16" data-id="element-5839">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5840">
                5️⃣ Le culpabilisateur subtil
              </h2>
              <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-8 rounded-r-xl" data-id="element-5841">
                <p className="italic text-gray-900 text-xl font-medium" data-id="element-5842">
                  "Je suppose que j'irai seul alors..."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600 mb-8" data-id="element-5843">
                L'IA marque cela comme une{' '}
                <strong className="text-gray-900" data-id="element-5844">manipulation passive</strong>.
              </p>
            </section>

            <section id="faux-supporter" className="mb-16" data-id="element-5845">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5846">
                6️⃣ Le faux supporter
              </h2>
              <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-8 rounded-r-xl" data-id="element-5847">
                <p className="italic text-gray-900 text-xl font-medium" data-id="element-5848">
                  "Je veux dire, tu as tendance à réagir de manière excessive
                  parfois."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5849">
                L'IA détecte la{' '}
                <strong className="text-gray-900" data-id="element-5850">critique masquée</strong>.
              </p>
            </section>

            <section id="healthy-toxic-combo" className="mb-16" data-id="element-5851">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5852">
                7️⃣ Le combo "Sain-Toxique"
              </h2>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 mb-8" data-id="element-5853">
                <p className="mb-4 text-lg text-gray-700" data-id="element-5854">
                  Certains jours chauds. Certains jours froids.
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600 mb-8" data-id="element-5855">
                L'IA étiquette ce schéma comme un{' '}
                <strong className="text-gray-900" data-id="element-5856">
                  rythme émotionnel instable
                </strong>
                .
              </p>
            </section>

            <section className="mb-16" data-id="element-5857">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5858">
                Pourquoi cela compte
              </h2>
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-10 mb-10 border border-red-100" data-id="element-5859">
                <p className="font-bold mb-8 text-2xl text-gray-900" data-id="element-5860">
                  Une fois que vous voyez le schéma, vous pouvez :
                </p>
                <ul className="space-y-5" data-id="element-5861">
                  <li className="flex items-start gap-4 text-lg text-gray-700" data-id="element-5862">
                    <span className="text-red-600 font-bold text-xl flex-shrink-0" data-id="element-5863">
                      ✓
                    </span>
                    <span data-id="element-5864">Protéger votre énergie</span>
                  </li>
                  <li className="flex items-start gap-4 text-lg text-gray-700" data-id="element-5865">
                    <span className="text-red-600 font-bold text-xl flex-shrink-0" data-id="element-5866">
                      ✓
                    </span>
                    <span data-id="element-5867">Fixer des limites</span>
                  </li>
                  <li className="flex items-start gap-4 text-lg text-gray-700" data-id="element-5868">
                    <span className="text-red-600 font-bold text-xl flex-shrink-0" data-id="element-5869">
                      ✓
                    </span>
                    <span data-id="element-5870">Arrêter de vous sentir coupable</span>
                  </li>
                </ul>
              </div>
            </section>

            <section id="faq" className="mb-20" data-id="element-5871">
              <h2 className="text-4xl font-bold mb-10 tracking-tight text-gray-900" data-id="element-5872">
                Questions Fréquentes
              </h2>
              <div className="space-y-5" data-id="element-5873">
                {faqSchema.mainEntity.map((faq, index) => <div key={index} className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5874">
                    <h3 className="font-bold text-xl mb-4 text-gray-900" data-id="element-5875">
                      {faq.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg" data-id="element-5876">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>)}
              </div>
            </section>
          </div>

          <div className="bg-red-600 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl" data-id="element-5877">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" data-id="element-5878">
              😮‍💨 Arrêtez de vous sentir épuisé.
              <br data-id="element-5879" />
              Commencez à voir clairement.
            </h2>
            <p className="text-red-100 mb-10 text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-5880">
              Arrêtez de deviner. Téléchargez vos textes sur Toxic Detector et
              obtenez un score de toxicité instantané.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-10 border border-white/20" data-id="element-5881">
              <p className="font-bold text-2xl" data-id="element-5882">
                "⚠️ Épuisement Émotionnel : Élevé. Communication unilatérale
                détectée."
              </p>
            </div>
            <Link to="/upload" className="inline-block bg-white text-red-600 px-12 py-5 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105" data-id="element-5883">
              Scannez vos chats maintenant →
            </Link>
          </div>

          <div className="mt-20" data-id="element-5884">
            <RelatedBlogPosts posts={[{
            title: 'Pourquoi certaines personnes nous épuisent-elles ? Partie 2',
            description: 'Plus de schémas subtils qui drainent votre énergie, y compris la surexplication et les je-sais-tout.',
            slug: 'emotional-drain-part-2',
            emoji: '😭'
          }, {
            title: 'Le Guide Ultime des Textes Passifs-Agressifs',
            description: 'Repérez le comportement passif-agressif et la manipulation cachée dans les SMS.',
            slug: 'ultimate-guide-passive-aggressive-texts',
            emoji: '😤'
          }]} data-id="element-5885" />
          </div>

          <div className="mt-16 text-center" data-id="element-5886">
            <button onClick={handleShare} className="inline-flex items-center gap-2 text-gray-700 hover:text-red-600 font-semibold transition-colors px-6 py-3 rounded-lg hover:bg-gray-50" data-id="element-5887">
              <Share2 size={20} data-id="element-5888" />
              Partager cet article
            </button>
          </div>
        </article>

        <footer className="bg-gray-900 text-white py-16 mt-24" data-id="element-5889">
          <div className="max-w-4xl mx-auto px-6" data-id="element-5890">
            <div className="border-t border-gray-800 pt-8 text-center" data-id="element-5891">
              <p className="text-gray-400 text-sm" data-id="element-5892">
                © {new Date().getFullYear()} ToxicDetector. Tous droits
                réservés.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>;
}